import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../utils/useApi';
import { useUserContext } from '../contexts/UserContext';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Sparkles,
  TrendingUp,
  Users,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  Calendar,
  Zap,
  Target,
  RefreshCw,
  X,
  ChevronRight,
  BarChart3,
  MousePointer2
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';



interface Lead {
  id: string;
  fullName: string;
  phoneNumber: string;
  createdAt: string;
  status: string;
}

interface LeadData {
  totalLeads: number;
  recentLeads: Lead[];
  conversionRate: number;
  totalMembers: number;
}

interface HealthMetrics {
  joinRate: number;
  renewalRate: number;
  attendanceTrend: number;
  healthScore: number;
  raw: {
    newMembersThisMonth: number;
    newMembersLastMonth: number;
    checkinsThisWeek: number;
    checkinsLastWeek: number;
    activeCount: number;
    expiredCount: number;
  };
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-950/90 backdrop-blur-md border border-zinc-800 p-3 rounded-xl shadow-2xl">
        <p className="text-xs font-black text-zinc-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-sm font-bold text-white">
          ₹{payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

const DashboardInfoModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  if (!isOpen) return null;

  const sections = [
    {
      title: "Today's Pulse",
      icon: <Activity className="w-5 h-5 text-indigo-500" />,
      items: [
        { label: "Gross Inflow", desc: "Real-time revenue capture from all purchases, renewals, and payments made within the current 24-hour cycle." },
        { label: "Admissions", desc: "Number of new members enrolled or plan purchases completed today." },
        { label: "Check-ins Today", desc: "Total footfall count for the day, including live attendance trends compared to previous averages." }
      ]
    },
    {
      title: "Predictive Gym Health",
      icon: <TrendingUp className="w-5 h-5 text-emerald-500" />,
      items: [
        { label: "Health Score", desc: "Our proprietary AI metric (0-100%) that cross-references retention, active engagement, and new growth to gauge overall business stability." },
        { label: "Joining Velocity", desc: "Compares current month's acquisition rate against the previous period to predict growth momentum." },
        { label: "Renewal Efficiency", desc: "A ratio of active vs. expired memberships. High efficiency indicates strong member lifetime value (LTV)." },
        { label: "Attendance Flow", desc: "Tracks active usage of the facility. Declining flow is often a leading indicator of upcoming churn." }
      ]
    },
    {
      title: "Financial Dynamics",
      icon: <BarChart3 className="w-5 h-5 text-blue-500" />,
      items: [
        { label: "Current MRR", desc: "Monthly Recurring Revenue based on active plan values. The predictable baseline of your business." },
        { label: "AI Forecast", desc: "Predictive revenue expectation for the next 30 days based on current growth and expiration patterns." }
      ]
    },
    {
      title: "Operational Queue",
      icon: <MousePointer2 className="w-5 h-5 text-orange-500" />,
      items: [
        { label: "Pending Actions", desc: "Critical renewals due within the next 5 days. These represent immediate revenue opportunities." },
        { label: "Lead Queue", desc: "New inquiries that haven't yet converted. Prioritized by AI to maximize sales conversion." }
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-950/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-[2.5rem] shadow-2xl flex flex-col border border-zinc-100">
        {/* Notion Style Header */}
        <div className="p-8 border-b border-zinc-50 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-zinc-50 rounded-2xl">
              <Sparkles className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-zinc-900 tracking-tight">Analytics Guide</h2>
              <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mt-0.5">Understanding your AI Insights</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-xl transition-colors">
            <X className="w-6 h-6 text-zinc-400" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-8 space-y-10">
          {sections.map((section, idx) => (
            <div key={idx} className="space-y-6">
              <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
                {section.icon}
                <h3 className="text-lg font-bold text-zinc-900 tracking-tight">{section.title}</h3>
              </div>
              <div className="grid gap-6">
                {section.items.map((item, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="mt-1.5 flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-500 transition-colors" />
                    </div>
                    <div>
                      <p className="font-black text-zinc-800 text-sm mb-1">{item.label}</p>
                      <p className="text-sm text-zinc-500 leading-relaxed font-medium">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Modal Footer */}
        <div className="p-6 bg-zinc-50 border-t border-zinc-100 text-center">
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">
            Data is synthesized across your gym's sales, attendance, and member retention records.
          </p>
        </div>
      </div>
    </div>
  );
};

export default function AIPage() {
  const { fetchWithAuth } = useApi();
  const navigate = useNavigate();
  const { userContext, loading: userLoading } = useUserContext();
  const [showInfoModal, setShowInfoModal] = useState(false);



  // Use React Query for consolidated dashboard data
  const { data, isLoading, refetch, isFetching } = useQuery(['dashboard-data', userContext?.user?.id], async () => {
    const [
      mrrResponse,
      renewalsResponse,
      checkinsResponse,
      leadsResponse,
      legacyPurchasesResponse,
      newPurchasesResponse,
      healthResponse,
      mainStatsResponse
    ] = await Promise.all([
      fetchWithAuth('/api/dashboard/mrr-forecast'),
      fetchWithAuth('/api/dashboard/renewals-due'),
      fetchWithAuth('/api/dashboard/checkins-today'),
      fetchWithAuth('/api/dashboard/leads'),
      fetchWithAuth('/api/purchases'),
      fetchWithAuth('/api/gym-purchases?limit=1000'),
      fetchWithAuth('/api/dashboard/health'),
      fetchWithAuth('/api/dashboard/main-stats')
    ]);

    return {
      metrics: {
        mrr: mrrResponse.success ? mrrResponse.data : null,
        renewals: renewalsResponse.success ? renewalsResponse.data : null,
        checkins: checkinsResponse.success ? checkinsResponse.data : null
      },
      leads: leadsResponse.success ? leadsResponse.data as LeadData : null,
      legacyPurchases: legacyPurchasesResponse.success ? legacyPurchasesResponse.data as any[] : [],
      newPurchases: newPurchasesResponse.success ? newPurchasesResponse.data as any[] : [],
      health: healthResponse.success ? healthResponse.data as HealthMetrics : null,
      revenueBreakdown: mainStatsResponse.success ? mainStatsResponse.data.revenueBreakdown : null
    };
  }, {
    enabled: !!userContext && !userLoading,
    staleTime: 5 * 60 * 1000,
  });

  const metrics = data?.metrics || { mrr: null, renewals: null, checkins: null };
  const leads = data?.leads || null;
  const health = data?.health || null;
  const revenueBreakdown = data?.revenueBreakdown || { plans: 0, products: 0, services: 0, other: 0 };

  const allPurchasesFlat = useMemo(() => {
    const legacy = (data?.legacyPurchases || []).map(p => ({
      ...p,
      source: 'legacy',
      date: p.purchaseDate,
      amount: Number(p.totalAmount) || ((Number(p.plan?.price || 0) - Number(p.discountAmount || 0)))
    }));
    
    const newer = (data?.newPurchases || []).map(p => ({
      ...p,
      source: 'new',
      date: p.createdAt,
      amount: Number(p.totalAmount)
    }));

    return [...legacy, ...newer].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [data?.legacyPurchases, data?.newPurchases]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Analytics for Cashflow
  const cashflowData = useMemo(() => {
    return Array.from({ length: 30 }).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      const dateStr = date.toDateString();
      const dayPurchases = allPurchasesFlat.filter(p => new Date(p.date).toDateString() === dateStr);
      return {
        date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        revenue: dayPurchases.reduce((sum, p) => sum + p.amount, 0)
      };
    });
  }, [allPurchasesFlat]);

  const todayPulse = useMemo(() => {
    const today = new Date().toDateString();
    const todayPurchases = allPurchasesFlat.filter(p => new Date(p.date).toDateString() === today);
    return {
      revenue: todayPurchases.reduce((sum, p) => sum + p.amount, 0),
      count: todayPurchases.length
    };
  }, [allPurchasesFlat]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
          <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-indigo-600 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12 animate-in fade-in duration-700">
      {/* Header with AI Title */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tighter flex items-center gap-3">
            <span className="ai-gradient-text">AI INSIGHTS</span>
            <div className="p-2 bg-indigo-100 rounded-2xl">
              <Sparkles className="w-6 h-6 text-indigo-600" />
            </div>
          </h1>
          <p className="text-zinc-500 font-bold uppercase tracking-widest text-xs mt-2">
            Intelligent Business Analytics & Predictive Health
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-2xl border border-zinc-100 shadow-sm">
            <Calendar className="w-4 h-4 text-zinc-400" />
            <span className="text-sm font-bold text-zinc-700">
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          <button
            onClick={() => setShowInfoModal(true)}
            className="p-2.5 bg-white border border-zinc-100 rounded-2xl shadow-sm hover:bg-zinc-50 transition-all active:scale-95"
            title="Dashboard Info"
          >
            <Info className="w-5 h-5 text-zinc-500" />
          </button>

          <button
            onClick={() => {
              refetch().then(() => toast.success('Data refreshed successfully'));
            }}
            disabled={isFetching}
            className={`p-2 bg-white border border-zinc-100 rounded-2xl shadow-sm group transition-all active:scale-95 ${isFetching ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-50 hover:border-indigo-100'}`}
            title="Refresh Data"
          >
            <RefreshCw className={`w-5 h-5 text-zinc-500 group-hover:text-indigo-600 transition-all ${isFetching ? 'animate-spin text-indigo-600' : ''}`} />
          </button>
        </div>
      </div>

      <DashboardInfoModal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Today's Pulse - DARK AI THEME */}
        <div className="lg:col-span-4 lg:row-span-2">
          <div className="ai-dark-card ai-glow-indigo h-full flex flex-col group transition-transform duration-500 hover:scale-[1.01]">
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Zap className="w-32 h-32 text-indigo-500" />
            </div>

            <div className="relative z-10 flex-1">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black tracking-tight flex items-center">
                    Today's Pulse
                  </h3>
                  <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Live Updates</p>
                </div>
              </div>

              <div className="space-y-8">
                <div className="p-6 bg-white/5 backdrop-blur-md rounded-[2rem] border border-white/10 transition-colors hover:bg-white/10">
                  <p className="text-[10px] font-black text-indigo-300 uppercase tracking-[0.2em] mb-2">Gross Inflow</p>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-3xl sm:text-5xl font-black tracking-tighter">₹{todayPulse.revenue.toLocaleString()}</span>
                    <div className="flex items-center text-emerald-400 text-[10px] sm:text-xs font-black">
                      <ArrowUpRight className="w-3 h-3 sm:w-4 h-4" />
                      NEW
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-white/5 backdrop-blur-md rounded-[2rem] border border-white/10 transition-colors hover:bg-white/10">
                  <p className="text-[10px] font-black text-indigo-300 uppercase tracking-[0.2em] mb-2">Admissions</p>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-3xl sm:text-5xl font-black tracking-tighter">{todayPulse.count}</span>
                    <span className="text-zinc-500 font-bold uppercase text-[8px] sm:text-[10px] tracking-widest">Plan Enrols</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white/5 relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1">Check-ins Today</p>
                  <p className="text-2xl font-black">{metrics.checkins?.count || 0}</p>
                </div>
                <div className={`px-3 py-1.5 rounded-xl font-black text-xs flex items-center gap-1 ${(metrics.checkins?.trend || 0) >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                  {(metrics.checkins?.trend || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {Math.abs(metrics.checkins?.trend || 0)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cashflow & Financials */}
        <div className="lg:col-span-8">
          <div className="ai-glass-card rounded-[2.5rem] p-8 h-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
              <div>
                <h3 className="text-xl font-black text-zinc-900 tracking-tight flex items-center">
                  Cashflow Dynamics (30D)
                </h3>
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Daily Revenue Streams</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Current MRR</p>
                  <p className="text-xl font-black text-indigo-600">{metrics.mrr ? formatCurrency(metrics.mrr.currentMRR) : '₹0'}</p>
                </div>
                <div className="w-px h-8 bg-zinc-200"></div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center justify-end">
                    Forecast
                  </p>
                  <p className="text-xl font-black text-zinc-900">{metrics.mrr ? formatCurrency(metrics.mrr.forecastMRR) : '₹0'}</p>
                </div>
              </div>
            </div>

            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cashflowData}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fontWeight: 900, fill: '#94a3b8' }}
                    interval={2}
                    dy={10}
                  />
                  <YAxis hide />
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#f1f5f9" />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#6366f1"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#colorRev)"
                    animationDuration={2000}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Revenue Distribution - NEW CATEGORIZATION */}
        <div className="lg:col-span-4">
          <div className="ai-glass-card rounded-[2.5rem] p-8 h-full flex flex-col">
            <h3 className="text-xl font-black text-zinc-900 tracking-tight mb-1">
              Revenue Distribution
            </h3>
            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-8">Split by Category</p>
            
            <div className="flex-1 flex flex-col items-center justify-center min-h-[180px]">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Plans', value: revenueBreakdown.plans },
                      { name: 'Products', value: revenueBreakdown.products },
                      { name: 'Services', value: revenueBreakdown.services },
                      { name: 'Other', value: revenueBreakdown.other }
                    ].filter(d => d.value > 0)}
                    innerRadius={65}
                    outerRadius={85}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="#6366f1" /> {/* Indigo for Plans */}
                    <Cell fill="#f43f5e" /> {/* Rose for Products */}
                    <Cell fill="#10b981" /> {/* Emerald for Services */}
                    <Cell fill="#94a3b8" /> {/* Slate for Other */}
                  </Pie>
                  <RechartsTooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-zinc-900 text-white p-3 rounded-xl border border-zinc-800 shadow-xl">
                            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">{payload[0].name}</p>
                            <p className="text-sm font-bold">{formatCurrency(payload[0].value as number)}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute flex flex-col items-center justify-center pointer-events-none">
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Total Gross</p>
                <p className="text-xl font-black text-zinc-900">
                  {formatCurrency(revenueBreakdown.plans + revenueBreakdown.products + revenueBreakdown.services + revenueBreakdown.other)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 mt-8">
              <div className="flex items-center justify-between p-3 bg-indigo-50/50 rounded-2xl border border-indigo-100/50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#6366f1]"></div>
                  <span className="text-xs font-bold text-zinc-700">Plans</span>
                </div>
                <span className="text-xs font-black text-zinc-900">{formatCurrency(revenueBreakdown.plans)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-rose-50/50 rounded-2xl border border-rose-100/50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#f43f5e]"></div>
                  <span className="text-xs font-bold text-zinc-700">Products</span>
                </div>
                <span className="text-xs font-black text-zinc-900">{formatCurrency(revenueBreakdown.products)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-2xl border border-emerald-100/50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]"></div>
                  <span className="text-xs font-bold text-zinc-700">Services</span>
                </div>
                <span className="text-xs font-black text-zinc-900">{formatCurrency(revenueBreakdown.services)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50/50 rounded-2xl border border-slate-100/50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#94a3b8]"></div>
                  <span className="text-xs font-bold text-zinc-700">Other</span>
                </div>
                <span className="text-xs font-black text-zinc-900">{formatCurrency(revenueBreakdown.other)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gym Health Representation */}
        <div className="lg:col-span-8">
          <div className="ai-glass-card rounded-[2.5rem] p-8 h-full">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-xl font-black text-zinc-900 tracking-tight flex items-center">
                  Predictive Gym Health
                </h3>
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Calculated by AI Assistant</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Health Index</p>
                  <p className={`text-2xl font-black ${(health?.healthScore || 0) > 70 ? 'text-emerald-600' :
                    (health?.healthScore || 0) > 40 ? 'text-amber-600' : 'text-red-600'
                    }`}>{health?.healthScore || 0}%</p>
                </div>
                <div className="w-14 h-14 flex items-center justify-center overflow-visible flex-shrink-0">
                  <PieChart width={56} height={56}>
                    <Pie
                      data={[
                        { value: health?.healthScore || 0 },
                        { value: 100 - (health?.healthScore || 0) }
                      ]}
                      innerRadius={16}
                      outerRadius={22}
                      startAngle={90}
                      endAngle={450}
                      dataKey="value"
                      stroke="none"
                      cx="50%"
                      cy="50%"
                    >
                      <Cell fill={(health?.healthScore || 0) > 70 ? '#10b981' : (health?.healthScore || 0) > 40 ? '#f59e0b' : '#ef4444'} />
                      <Cell fill="#f4f4f5" />
                    </Pie>
                  </PieChart>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Metric 1: Joining Rate */}
              <div className="p-6 bg-zinc-50 rounded-3xl border border-zinc-100 group hover:border-indigo-200 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-indigo-100 rounded-xl">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className={`text-xs font-black flex items-center gap-1 ${(health?.joinRate || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                    {(health?.joinRate || 0) >= 0 ? '+' : ''}{health?.joinRate || 0}%
                  </div>
                </div>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-1 flex items-center">
                  Joining Velocity
                </p>
                <p className="text-2xl font-black text-zinc-900">{health?.raw.newMembersThisMonth || 0}</p>
                <p className="text-[10px] text-zinc-500 font-bold mt-1">New this month</p>
              </div>

              {/* Metric 2: Renewal Efficiency */}
              <div className="p-6 bg-zinc-50 rounded-3xl border border-zinc-100 group hover:border-fuchsia-200 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-fuchsia-100 rounded-xl">
                    <Zap className="w-5 h-5 text-fuchsia-600" />
                  </div>
                  <div className="text-xs font-black text-fuchsia-600">
                    {health?.renewalRate || 0}%
                  </div>
                </div>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-1 flex items-center">
                  Renewal Efficiency
                </p>
                <p className="text-2xl font-black text-zinc-900">{health?.raw.activeCount || 0}</p>
                <p className="text-[10px] text-zinc-500 font-bold mt-1">Active memberships</p>
              </div>

              {/* Metric 3: Attendance Pulse */}
              <div className="p-6 bg-zinc-50 rounded-3xl border border-zinc-100 group hover:border-cyan-200 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-cyan-100 rounded-xl">
                    <Target className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div className={`text-xs font-black flex items-center gap-1 ${(health?.attendanceTrend || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                    {(health?.attendanceTrend || 0) >= 0 ? '+' : ''}{health?.attendanceTrend || 0}%
                  </div>
                </div>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-1 flex items-center">
                  Attendance Flow
                </p>
                <p className="text-2xl font-black text-zinc-900">{health?.raw.checkinsThisWeek || 0}</p>
                <p className="text-[10px] text-zinc-500 font-bold mt-1">Visits this week</p>
              </div>
            </div>
          </div>
        </div>


        {/* Quick Actions & Other Metrics */}
        <div className="lg:col-span-4 space-y-8">
          {/* Renewals Summary */}
          <div className="ai-glass-card rounded-[2.5rem] p-8 border-l-4 border-l-orange-400">
            <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-4 flex items-center">
              Pending Actions
            </h4>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-black text-zinc-900">{metrics.renewals?.count || 0}</p>
                <p className="text-xs text-zinc-500 font-bold mt-1">Renewals next 5 days</p>
              </div>
              <button
                onClick={() => navigate('/members?renewalFilter=5')}
                className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center hover:bg-orange-600 hover:text-white transition-all shadow-lg active:scale-95"
              >
                <ArrowUpRight className="w-5 h-5" />
              </button>
            </div>
            {metrics.renewals && metrics.renewals.totalRevenue > 0 && (
              <p className="mt-4 text-[10px] font-black text-emerald-600 uppercase bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100 w-fit">
                Potential: {formatCurrency(metrics.renewals.totalRevenue)}
              </p>
            )}
          </div>

          {/* Lead Quick Queue */}
          <div className="ai-glass-card rounded-[2.5rem] p-8">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] flex items-center">
                Conversion Queue
              </h4>
              <button onClick={() => navigate('/members?filter=lead')} className="text-[10px] font-black text-indigo-600 uppercase hover:underline">View All</button>
            </div>

            <div className="space-y-4">
              {leads && leads.recentLeads.length > 0 ? (
                leads.recentLeads.map((lead) => (
                  <div key={lead.id} className="group flex items-center gap-3 p-3 rounded-2xl border border-transparent hover:border-indigo-100 hover:bg-indigo-50 transition-all cursor-pointer" onClick={() => navigate(`/members?search=${lead.phoneNumber}`)}>
                    <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center text-sm font-black text-zinc-500 group-hover:bg-white group-hover:text-indigo-600 transition-colors">
                      {(lead.fullName || 'U').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black text-zinc-900 truncate">{lead.fullName || 'Unknown'}</p>
                      <p className="text-[10px] font-bold text-zinc-400">{lead.phoneNumber}</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Zap className="w-4 h-4 fill-current" />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-zinc-400 font-bold italic text-center py-4">Queue is empty</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}